export const EXPERIMENT_DEFAULT_TEMPLATE = `def write_your_model_experiment(
    dataset: pd.DataFrame | None = None,
    featureset: pd.DataFrame | None = None,
    parameterset: dict[str, Any] | None = None,
) -> ExperimentLogBundle:
    """학습·평가를 구현하고, 아래 변수를 채운 뒤 반환하세요.

    **채울 변수**

    - \`\`params\`\`: 실험에 설정한 하이퍼파라미터 딕셔너리
    - \`\`metrics\`\`: 학습 종료 후 기록할 최종 성과 지표 (단일 숫자)
    - \`\`metric_series\`\`: 학습 과정 중 변화를 기록할 시계열 지표 (Loss 등)
    - \`\`model\`\`: 저장 및 배포 대상인 최종 학습 완료 모델 객체
    - \`\`model_input_sample\`\`: 모델의 입력 형태를 정의하기 위한 데이터 샘플 일부
    - \`\`output_datasets\`\`: 영구 저장할 가공된 데이터프레임
    - \`\`artifact_files\`\`: 이미지, 로그 파일 등 모델 외에 보관할 로컬 파일들
    - \`\`parameterset_description\`\`: 파라미터 구성에 대한 추가 설명
    - \`\`artifacts_description\`\`: 업로드된 아티팩트 묶음에 대한 설명


    Args:
        dataset: 런을 생성할 때 선택한 dataset. DATASET_TABLE이 None이면 None.
        featureset: 런을 생성할 때 선택한 featureset. FEATURE_TABLE이 None이면 None.
        parameterset: 런을 생성할 때 선택한 parameterset. ParamContext·외부 주입 둘 다 없으면 None.
    """
    # ⚠️  아래 변수명은 수정하지 마세요. return 문이 이 이름을 그대로 사용합니다.
    params: dict[str, Any] = dict(parameterset) if parameterset is not None else {}
    metrics: dict[str, float] = {}
    metric_series: list[MetricRecord] = []
    model_input_sample: Any | None = None
    model: Any | None = None
    output_datasets: list[OutputDataset] = []
    artifact_files: list[ArtifactFile] = []
    parameterset_description: str = ""
    artifacts_description: str = ""

    # ── 여기에 실험 로직을 작성하세요 ──────────────────────────────────────
    #
    # [params] 하이퍼파라미터 기록
    #   params["n_estimators"] = 100
    #   params["learning_rate"] = 0.01
    #
    # [metrics] 최종 지표 (학습 후 한 번만)
    #   metrics["accuracy"] = 0.95
    #   metrics["f1_score"] = 0.93
    #
    # [metric_series] 에폭/스텝별 곡선 지표
    #   for epoch, loss in enumerate(train_losses):
    #       metric_series.append(MetricRecord("train_loss", loss, step=epoch))
    #
    # [model] 저장할 모델 객체 (하나만)
    #   model = RandomForestClassifier(**params)
    #   model.fit(X_train, y_train)
    #   model_input_sample = X_train.iloc[:5]   # 스키마 추론용 샘플 (선택)
    #
    # [output_datasets] 생성한 DataFrame을 Iceberg에 저장
    #   result_df = pd.DataFrame(predictions, columns=["pred"])
    #   output_datasets.append(OutputDataset(
    #       df=result_df,
    #       table_name="my_predictions",
    #       output_type="dataset",       # "dataset" | "featureset"
    #       description="모델 예측 결과",
    #   ))
    #
    # [artifact_files]
    #   artifacts_description = "학습 결과물 모음"
    #   artifact_files.append(ArtifactFile(local_path="report.html"))
    #
    # [parameterset_description] 파라미터 세트 설명 (선택)
    #   parameterset_description = "v2 하이퍼파라미터 탐색 결과"

  
    return ExperimentLogBundle(
        params=params,
        metrics=metrics,
        metric_series=metric_series,
        model_input_sample=model_input_sample,
        model=model,
        output_datasets=output_datasets,
        artifact_files=artifact_files,
        parameterset_description=parameterset_description,
        artifacts_description=artifacts_description,
    )
`;

export const generateExperimentFunctionSignature = (): string => {
  return `def write_your_model_experiment(
    dataset: pd.DataFrame | None = None,
    featureset: pd.DataFrame | None = None,
    parameterset: dict[str, Any] | None = None,
) -> ExperimentLogBundle:`;
};

export const splitExperimentTemplateCode = (templateCode: string): { body: string } => {
  // 1. 특정 함수명으로 매칭 시도 (하위 호환성)
  const specificMatch = templateCode.match(/def write_your_model_experiment\([\s\S]*?\)\s*->\s*ExperimentLogBundle:\n/);

  if (specificMatch && specificMatch.index !== undefined) {
    const body = templateCode.substring(specificMatch.index + specificMatch[0].length);
    return { body };
  }

  // 2. 일반적인 함수 정의로 매칭 (import 문 제거)
  // 첫 번째 'def'로 시작하는 함수 정의를 찾아서 그 이후를 반환
  const genericMatch = templateCode.match(/def\s+\w+\s*\([\s\S]*?\)\s*->\s*ExperimentLogBundle:\n/);

  if (genericMatch && genericMatch.index !== undefined) {
    const body = templateCode.substring(genericMatch.index + genericMatch[0].length);
    return { body };
  }

  // 3. 매칭 실패 시: import 문만 제거하고 나머지 반환
  const lines = templateCode.split('\n');
  let firstDefIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith('def ')) {
      firstDefIndex = i;
      break;
    }
  }

  if (firstDefIndex >= 0) {
    // 'def'로 시작하는 줄부터 끝까지 반환
    const codeFromDef = lines.slice(firstDefIndex).join('\n');

    // 시그니처 이후 본문만 추출 시도
    const signatureEnd = codeFromDef.indexOf(':\n');
    if (signatureEnd !== -1) {
      return { body: codeFromDef.substring(signatureEnd + 2) };
    }

    return { body: codeFromDef };
  }

  // 4. 'def'도 없으면 전체 반환
  return { body: templateCode };
};

type PrepareExperimentCodeParams = {
  initialCode: string | undefined;
  templateCode?: string;
};

export const prepareExperimentCode = ({
  initialCode,
  templateCode = EXPERIMENT_DEFAULT_TEMPLATE,
}: PrepareExperimentCodeParams): { signature: string; body: string } => {
  const signature = generateExperimentFunctionSignature();

  if (initialCode) {
    const { body } = splitExperimentTemplateCode(initialCode);
    return { signature, body };
  }

  const { body } = splitExperimentTemplateCode(templateCode);
  return { signature, body };
};
